import type { CSSProperties } from 'react'
import { Fragment, useEffect, useState } from 'react'

import { useAppSelector } from 'hooks/reduxHooks'
import {
  describeGhaPageSurface,
  describeGhaUiBlocker,
  describeSsrDelivery,
  elapsedMs,
  formatElapsed,
  GHA_CLOCK_CLASS,
  GHA_CLOCK_CSS,
  GHA_CLOCK_REELS,
  GHA_STEPS,
  getGhaStepsMask,
  isGhaVerifier,
  serverTimeMs,
} from 'lib/metasearch/ghaLoadingDiagnostics'

const PANEL_STYLE: CSSProperties = {
  position: 'fixed',
  top: 8,
  left: 8,
  zIndex: 2147483647,
  padding: '8px 12px',
  background: 'rgba(0, 0, 0, 0.85)',
  color: '#fff',
  font: '12px/1.6 ui-monospace, monospace',
  borderRadius: 4,
  pointerEvents: 'none',
  whiteSpace: 'pre',
}

/** Each digit owns a full line box so one `steps()` step scrolls exactly one digit. */
const DIGIT_STYLE: CSSProperties = { display: 'block' }

const CLOCK_PLACEHOLDER = '0:00.00'
const ELAPSED_LABEL = 'elapsed '
const SSR_LABEL = 'ssr     '
const SSR_SOURCE = '(ssr)'
const HYDRATED_LABEL = 'Hydrated'
const PAGE_CHUNK_LABEL = 'Page chunk'
const SURFACE_LABEL = 'Surface'
/** Brief linger so final timings stay readable after all steps complete. */
const LINGER_AFTER_DONE_MS = 5000

/** Distinguishes full-page load (timer from nav start) from client-side nav. */
let hasMountedInitialPageLoad = false

/** Bot-only overlay for GHA price-validation screenshots. */
function GhaLoadingDiagnostics() {
  const [startMs] = useState(() => {
    // Don't mutate the module flag during SSR — it would leak across requests.
    if (typeof window === 'undefined') return 0

    if (hasMountedInitialPageLoad) return elapsedMs()

    hasMountedInitialPageLoad = true
    return 0
  })
  const [doneAt, setDoneAt] = useState<Record<string, number>>({})
  const [hydratedAt, setHydratedAt] = useState<number | undefined>()
  const [pageChunkAt, setPageChunkAt] = useState<number | undefined>()
  const mask = useAppSelector(getGhaStepsMask)
  // Capture SSR-delivered steps on first render; null after client-side nav.
  const [ssrMask] = useState<string | null>(() => (startMs === 0 ? mask : null))
  const [lingerExpired, setLingerExpired] = useState(false)

  const enabled = useAppSelector(isGhaVerifier)
  const pageSurface = useAppSelector(describeGhaPageSurface)
  const uiBlocker = useAppSelector(state => describeGhaUiBlocker(state, {
    hydrated: hydratedAt !== undefined,
    pageChunkReady: pageChunkAt !== undefined,
  }))
  const dataDone = mask.length > 0 && !mask.includes('0')
  const allDone = dataDone && hydratedAt !== undefined && pageChunkAt !== undefined && uiBlocker === 'ui: ready'

  // Effects only run after hydration — first paint of this effect is "React interactive".
  useEffect(() => {
    if (!enabled || hydratedAt !== undefined) return
    window.__GHA_CLIENT_LOGGER__?.markHydrated()
    setHydratedAt(elapsedMs())
  }, [enabled, hydratedAt])

  // Same dynamic import loadable(`BedbankOfferPage`) uses; fallback spinner shows until this resolves.
  useEffect(() => {
    if (!enabled || pageChunkAt !== undefined) return

    let cancelled = false
    import('components/Pages/BedbankOfferPage')
      .then(() => {
        if (!cancelled) setPageChunkAt(elapsedMs())
      })
      .catch(error => {
        window.__GHA_CLIENT_LOGGER__?.reportPartnerPageChunkFailure(error)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, pageChunkAt])

  useEffect(() => {
    if (!enabled) return

    setDoneAt(previous => {
      const newlyDone = GHA_STEPS.filter((step, index) => mask[index] === '1' && previous[step.id] === undefined)
      if (!newlyDone.length) return previous

      const at = elapsedMs()
      return {
        ...previous,
        ...Object.fromEntries(newlyDone.map(step => [step.id, at])),
      }
    })
  }, [enabled, mask])

  useEffect(() => {
    if (!enabled || !allDone) return

    const timeoutId = window.setTimeout(() => setLingerExpired(true), LINGER_AFTER_DONE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [enabled, allDone])

  useEffect(() => {
    if (!enabled) return

    window.__GHA_CLIENT_LOGGER__?.updateDiagnostics({
      ghaStepsMask: mask,
      ghaUiBlocker: uiBlocker,
    })
    if (allDone) window.__GHA_CLIENT_LOGGER__?.markReady()
  }, [enabled, mask, uiBlocker, allDone])

  if (!enabled || lingerExpired) return null

  const serverMs = serverTimeMs()
  const serverTiming = serverMs === undefined ? CLOCK_PLACEHOLDER : formatElapsed(serverMs)
  const [ssrVerdict, ssrDetail] = describeSsrDelivery(ssrMask)
  const ssrRow = `${SSR_LABEL}${serverTiming}  ${ssrVerdict}`
  const ssrDetailRow = ssrDetail && `${' '.repeat(ssrRow.length - ssrVerdict.length)}${ssrDetail}`
  const hydratedTick = hydratedAt === undefined ? '[ ]' : '[x]'
  const hydratedTiming = hydratedAt === undefined ? '' : formatElapsed(hydratedAt - startMs)
  const hydratedRow = `${hydratedTick} ${HYDRATED_LABEL.padEnd(17)}${hydratedTiming}`
  const pageChunkTick = pageChunkAt === undefined ? '[ ]' : '[x]'
  const pageChunkTiming = pageChunkAt === undefined ? '' : formatElapsed(pageChunkAt - startMs)
  const pageChunkRow = `${pageChunkTick} ${PAGE_CHUNK_LABEL.padEnd(17)}${pageChunkTiming}`
  const pageSurfaceRow = `${SURFACE_LABEL} ${pageSurface}`

  return (
    <div style={PANEL_STYLE} data-testid="gha-loading-diagnostics">
      <style dangerouslySetInnerHTML={{ __html: GHA_CLOCK_CSS }} />
      {/* Freezing on the final value is safe: by then the main thread is free again. */}
      <div className={allDone ? `${GHA_CLOCK_CLASS}-frozen` : undefined}>
        {ELAPSED_LABEL}
        {GHA_CLOCK_REELS.map(reel => (
          <Fragment key={reel.name}>
            <span className={`${GHA_CLOCK_CLASS}-reel ${GHA_CLOCK_CLASS}-${reel.name}`}>
              <span>
                {Array.from({ length: reel.digits }, (_, digit) => (
                  <span key={digit} style={DIGIT_STYLE}>{digit}</span>
                ))}
              </span>
            </span>
            {reel.separator}
          </Fragment>
        ))}
      </div>
      <div suppressHydrationWarning>{ssrRow}</div>
      {!!ssrDetailRow && <div>{ssrDetailRow}</div>}
      <div>{pageSurfaceRow}</div>
      <div>{uiBlocker}</div>
      <div>{hydratedRow}</div>
      <div>{pageChunkRow}</div>
      {GHA_STEPS.map((step, index) => {
        const tick = mask[index] === '1' ? '[x]' : '[ ]'
        // Steps present in the initial Redux snapshot show (ssr); later client fills keep a clock.
        const fromSsr = ssrMask?.[index] === '1'
        const timing = fromSsr
          ? SSR_SOURCE
          : doneAt[step.id] === undefined ? '' : formatElapsed(doneAt[step.id] - startMs)
        const row = `${tick} ${step.label.padEnd(17)}${timing}`

        return <div key={step.id}>{row}</div>
      })}
    </div>
  )
}

export default GhaLoadingDiagnostics
